import React, { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AddUpdateInstrument } from 'api/Trading-Accounts/Types/AddUpdateInstrument'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { InputFilterCell, selectFilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchTradingAccountInstrumentTypeAction,
  getCurrenciesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import { AddEditInstrumentPage } from './AddEditInstrumentPage'

const InstrumentFilterCell = (props: GridFilterCellProps) => {
  const { tradingAccountInstrumentTypes: instrumentTypes } = useMapStateToProps()

  return selectFilterCell({
    items: instrumentTypes.map((x) => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.name,
    })),
    options: {
      operator: '==',
    },
  })(props)
}

const useMapStateToProps = () => ({
  tradingAccountInstrumentTypes: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountInstrumentType
  ),
  currencyTypes: useAppSelector((state) => state.tradingAccounts.currencies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchTradingAccountInstrumentTypes: async () =>
      dispatch(fetchTradingAccountInstrumentTypeAction(tradingAccountsApiClient)),
    fetchCurrenciesActionTypes: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
  }
}

export const InstrumentsPage: React.FC = () => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean
    data?: undefined | InstrumentDto
  }>({
    visible: false,
  })

  const crumbs = [{ title: 'Instruments', link: links.brokerageInstruments.url }]

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { tradingAccountInstrumentTypes: instrumentTypes, currencyTypes } = useMapStateToProps()
  const { fetchTradingAccountInstrumentTypes, fetchCurrenciesActionTypes } = useMapDispatchToProps()

  const [instrumentModal, setInstrumentModal] = useState<{
    data?: InstrumentDto<string>
    visible: boolean
  }>({
    visible: false,
  })

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    return tradingAccountsApiClient.getInstruments()
  }, [])
  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (isEmpty(instrumentTypes)) {
      fetchTradingAccountInstrumentTypes()
    }
    if (isEmpty(currencyTypes)) {
      fetchCurrenciesActionTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirmation = (e: InstrumentDto) => {
    setDeleteModal({ data: e, visible: true })
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data && data.length >= 1) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handleAddEditInstrument = (instrumentDto?: InstrumentDto<string>) => {
    setInstrumentModal({
      visible: true,
      data: instrumentDto,
    })
  }

  const handleDeleteEditInstrument = async () => {
    if (deleteModal.data) {
      await tradingAccountsApiClient.deleteInstrument(deleteModal.data.id)
      setToast(successToast(`Instrument ${deleteModal.data.symbol} deleted`))
      forceRefresh()
      setDeleteModal({ visible: false, data: undefined })
    }
  }

  const onSubmitAddEditInstrument = async (values: AddUpdateInstrument<string>) => {
    const isUpdate = !!values.id
    if (isUpdate) {
      await tradingAccountsApiClient.updateInstrument(values)
    } else {
      await tradingAccountsApiClient.addInstrument(values)
    }
    setInstrumentModal({ visible: false, data: undefined })
    setToast(successToast(`Instrument ${isUpdate ? 'updated' : 'added'}`))
    forceRefresh()
  }

  const gridData = filterBy(data || [], filter).sort((a, b) => stringComparator(a.symbol, b.symbol))

  return (
    <Loading isLoading={isLoading}>
      {instrumentModal.visible && (
        <AddEditInstrumentPage
          isOpen={instrumentModal.visible}
          onClose={() => setInstrumentModal({ visible: false, data: undefined })}
          instrument={instrumentModal.data}
          currencies={currencyTypes}
          onSubmit={onSubmitAddEditInstrument}
        />
      )}
      <Breadcrumbs crumbs={crumbs} />
      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditInstrument)}
        text='Add instrument'
        onClick={handleAddEditInstrument}
      />
      <Grid
        resizable
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        pageable={pageableSettings}
        data-test='table'
      >
        <Column field='symbol' title='Symbol' filterCell={InputFilterCell} />
        <Column field='name' title='Name' filterCell={InputFilterCell} />
        <Column
          field='type.id'
          cell={(gridCellProps) => {
            const { type } = gridCellProps.dataItem
            const foundObject = instrumentTypes.find((i) => {
              return i.id === type.id
            })
            return <td>{foundObject?.name}</td>
          }}
          title='Type'
          filterCell={InstrumentFilterCell}
        />
        <Column field='withholdingTax' title='Withholding Tax' filterCell={InputFilterCell} />
        <Column field='contractSize' title='Contract size' filterCell={InputFilterCell} />
        <Column
          field='baseCurrency.id'
          cell={(gridCellProps) => {
            const { baseCurrency } = gridCellProps.dataItem
            return <td>{baseCurrency?.id || 'N/A'}</td>
          }}
          title='Base currency'
          filterCell={InputFilterCell}
        />
        <Column field='digits' title='Digits' filterCell={InputFilterCell} />
        <Column
          title=''
          width={50}
          filterable={false}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    disabled: !hasPermission(permissions, PermissionsEnum.CanEditInstrument),
                    onClick: () => handleAddEditInstrument(gridCellProps.dataItem),
                  },
                  {
                    name: 'Delete',
                    disabled: !hasPermission(permissions, PermissionsEnum.CanDeleteInstrument),
                    onClick: () =>
                      handleDeleteConfirmation(gridCellProps.dataItem as InstrumentDto),
                  },
                ]}
              />
            </td>
          )}
        />
      </Grid>
      {!!deleteModal.visible && (
        <ConfirmationModal
          contentText={`Are you sure you want to delete ${deleteModal.data?.symbol} ${deleteModal.data?.name}`}
          yesButtonText='Yes, delete'
          onYes={handleDeleteEditInstrument}
          onCancel={() => setDeleteModal({ data: undefined, visible: false })}
          visible={!!deleteModal.visible}
        />
      )}
    </Loading>
  )
}
